"use client";

import { useEffect } from "react";
import Link from "next/link";

/**
 * Global error boundary — caught at the route layer for any uncaught
 * runtime error. Keeps the same editorial frame as 404 so visitors don't
 * see a generic Next.js fallback.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("[error.tsx]", error);
  }, [error]);

  return (
    <main className="relative overflow-hidden min-h-screen flex items-center bg-[var(--color-navy)] text-[var(--color-text)]">
      <div className="mx-auto max-w-[680px] px-[var(--spacing-page-x)] py-16">
        {/* plain <img>: error UI must not depend on the image optimizer */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/brand/amalbryx-ab-wolf-square.png"
          alt=""
          aria-hidden
          width={144}
          height={144}
          className="h-14 w-auto mb-6 [filter:drop-shadow(0_0_14px_rgba(59,130,246,0.2))]"
        />
        <div className="microcaps text-[var(--color-blue)] mb-3">
          ERROR · 500 / UNCAUGHT
          {error.digest ? (
            <span className="ml-2 text-[var(--color-text-mute)]">
              · {error.digest}
            </span>
          ) : null}
        </div>
        <h1
          className="text-[clamp(48px,8vw,96px)] leading-[0.95] tracking-[-0.025em] mb-6"
          style={{
            fontFamily: "var(--font-display)",
            fontVariationSettings: '"wght" 800, "wdth" 75',
          }}
        >
          Something broke.
        </h1>
        <p className="text-lg text-[var(--color-text-dim)] max-w-[52ch] mb-10">
          The build hit an exception we didn&apos;t plan for. The error has been
          logged. You can retry or head home — both are fine.
        </p>
        <div className="flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() => reset()}
            className="microcaps inline-flex items-center gap-2 rounded-md bg-[var(--color-blue)] text-[var(--color-navy)] px-5 py-3"
          >
            Try again
          </button>
          <Link
            href="/"
            className="microcaps inline-flex items-center gap-2 rounded-md border border-[var(--color-hairline-strong)] text-[var(--color-text)] px-5 py-3 hover:border-[var(--color-blue)] transition-colors"
          >
            Back home
            <span aria-hidden className="arrow-rtl-flip">→</span>
          </Link>
        </div>
      </div>
    </main>
  );
}
